/**
 * @file Component to present forecast controls
 * @author Heino Lenting, Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef } from '@angular/core';
import { Entity } from 'breeze-client';
import { LocalisationService } from '../../core/localisation.service';
import { ForecastService } from '../forecast.service';
import { PluginViewBase } from '../../grids/plugins/plugin-view/plugin-view.base';
import { EventsService } from '../../core/event.service';
import { DomainService } from '../../domain.service';
import { Processor } from '../../grids/processors/processor';
import { FormControl } from '@angular/forms';
import { SettingsService } from '../../settings.service';
import { GridService } from '../../grids/grid.service';
/**
 * Class to show the forecast plugins.
 */
var PluginViewControlsComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewControlsComponent, _super);
    /**
     * Creates an instance of PluginForcastControlsComponent.
     * @param {LocalisationService} localisationService The localisation service.
     * @param {ChangeDetectorRef} changeDetector The change detector.
     * @param {SettingsService} settingsService The settings service.
     * @param {ForecastService} forecastService The forecast service.
     * @param {AlertsService} alertsService The service can be used to display alerts.
     * @memberOf PluginForcastControlsComponent
     */
    function PluginViewControlsComponent(localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, domainService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService) || this;
        _this.gridService = gridService;
        _this.forecastService = forecastService;
        _this.domainService = domainService;
        _this.pluginForcastControl = new FormControl();
        /**
         * A value indicating whether the forecast is being calculated.
         * @memberOf PluginForcastControlsComponent
         */
        _this.isCalculating = false;
        /**
         * A value indicating whether the forecast is being planned.
         * @memberOf PluginForcastControlsComponent
         */
        _this.isExecuting = false;
        /**
        * Holds the reference of the component format.
        * @private
        * @type {ComponentConfiguration}
        */
        _this._format = undefined;
        _this.autoplan = settingsService.settings.autoplan;
        _this.isExecuting = _this.forecastService.isExecuting;
        _this.isCalculating = _this.forecastService.isCalculating;
        _this.subscriptionPool.add(_this.forecastService.processCompleted.subscribe(function () {
            _this.isExecuting = false;
            _this.isCalculating = false;
            _this.detectChanges();
        }));
        return _this;
    }
    Object.defineProperty(PluginViewControlsComponent.prototype, "template", {
        /**
         * Gets or sets the shift template.
         * @readonly
         * @type {Entity}
         * @memberOf PluginForcastControlsComponent
         */
        get: function () {
            return this._template;
        },
        set: function (value) {
            if (value === this._template) {
                return;
            }
            this._template = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "selectedFunction", {
        /**
        * Gets or sets the selected function from the plugin selector
        */
        get: function () {
            return this._selectedFunction;
        },
        set: function (entity) {
            this._selectedFunction = entity;
            this._template = null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "templateQuery", {
        /**
         * Returns the query for looking for the template of a specific function.
         * @readonly
         * @type {*}
         * @memberOf PluginForcastViewControlsComponent
         */
        get: function () {
            var name = this.format.fields.find(function (field) { return field.name === 'Name'; });
            var number = this.format.fields.find(function (field) { return field.name === 'Number'; });
            var query = {
                dataSource: this.format.dataSource,
                entity: this.format.entity,
                fields: [name.name, number.name],
                displayFormat: "[" + name.name + "], [" + number.name + "]",
                parameters: {
                    function: this._selectedFunction ? this._selectedFunction['Oid'] : '?'
                }
            };
            return query;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "scriptQuery", {
        get: function () {
            var query = {
                dataSource: 'PlanMacroItems',
                entity: 'PlanMacro',
                fields: ['Name'],
                displayFormat: '[Name]'
            };
            return query;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "pluginKey", {
        /**
         * Gets the plugin key. The key to store and find the timeslot collections of this plugin.
         * @readonly
         * @public
         * @type {string}
         * @memberOf PluginViewBase
         */
        get: function () {
            return Processor.pluginKey + '-view-forecast-controls';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "script", {
        /**
         * Gets or sets the plan macro.
         * @readonly
         * @type {PlanMacro}
         * @memberOf PluginForcastControlsComponent
         */
        get: function () {
            return this._script;
        },
        set: function (value) {
            if (value === this._script) {
                return;
            }
            this._script = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewControlsComponent.prototype, "format", {
        /**
         * Get the format of the lookup.
         */
        get: function () {
            return this._format;
        },
        /**
         * Sets the format of the lookup
         */
        set: function (value) {
            if (value === this._format) {
                return;
            }
            this._format = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Start the execution flow if a execution or calculation process is not already running and a shift template is selected.
     * @memberOf PluginForcastControlsComponent
     */
    PluginViewControlsComponent.prototype.execute = function () {
        this.isExecuting = true;
        this.detectChanges();
        this.forecastService.execute(this.domainService, this.selectedFunction, this.template, this.script, this.gridService.startDate, this.gridService.endDate);
    };
    /**
     * Start the calculation flow if a execution or calculation process is not already running and a shift template is selected.
     * @memberOf PluginForcastControlsComponent
     */
    PluginViewControlsComponent.prototype.calculate = function () {
        this.isCalculating = true;
        this.detectChanges();
        this.forecastService.calculate(this.selectedFunction, this.gridService.startDate, this.gridService.endDate);
    };
    /**
     * Returns true if the all conditions are met to execute planning.
     * @returns {boolean} A value indicating whether the planning can be executed.
     * @memberOf PluginForcastViewControlsComponent
     */
    PluginViewControlsComponent.prototype.readyToExecute = function () {
        return !this.isExecuting && !this.isCalculating && this.selectedFunction != null && this.template != null;
    };
    /**
     * Returns true if the all conditions are met to calculate calls.
     * @returns {boolean} A value indicating whether calls can be calculated.
     * @memberOf PluginForcastViewControlsComponent
     */
    PluginViewControlsComponent.prototype.readyToCalculate = function () {
        return !this.isExecuting && !this.isCalculating && this.selectedFunction != null;
    };
    /**
     * Refreshes the layout.
     * @memberOf PluginForcastViewControlsComponent
     */
    PluginViewControlsComponent.prototype.templateChanged = function () {
        this.detectChanges();
    };
    /**
     * Filters out tasks that are already fully planned.
     * @protected
     * @param {Entity} item The task.
     * @returns A value indicating whether the task should be included.
     * @memberOf PluginForcastControlsComponent
     */
    PluginViewControlsComponent.prototype.itemFilter = function (item) {
        return !item.isFullyPlanned();
    };
    return PluginViewControlsComponent;
}(PluginViewBase));
export { PluginViewControlsComponent };
